/**
 * Briefing du niveau : description et ennemis rencontrés (écran de jeu).
 *
 * Trois familles de clés de contenu sont consommées **ici et seulement ici**, par convention
 * d’identifiant (DECISIONS §9.2) :
 * - `content:niveau.<id>.desc` — la description écrite par eq-game-design (41 clés de `niv_001` à
 *   `niv_040` plus `niv_illimite`) ;
 * - `content:da.<da>.name` et `.desc` — la direction artistique du niveau, lue dans le champ `da`
 *   du niveau (4 valeurs : feu, eau, terre, air) ;
 * - `content:ennemi.<id>.name` et `.desc` — la fiche des cinq ennemis, affichée pour ceux qui
 *   apparaissent réellement dans les vagues du niveau.
 *
 * Soit 41 + 8 + 10 = **59 clés** qui existaient dans le catalogue sans être lues (écart D-M3-06) :
 * elles sont désormais référencées par l’interface, sans qu’aucune valeur de contenu ne change
 * (A-40). Aucune n’est supprimée : le texte écrit par eq-game-design reste disponible.
 */
import { useTranslation } from 'react-i18next';

import { cleTraductionContenu } from '../i18n';

import type { Niveau } from '@elemora/shared/content';
import type { ReactNode } from 'react';

/** Ennemi présent dans un niveau : nombre de vagues où il apparaît, et statut de boss. */
interface EnnemiDuNiveau {
  readonly id: string;
  readonly vagues: number;
  readonly boss: boolean;
}

/**
 * Résume les ennemis d’un niveau.
 *
 * Un ennemi qui apparaît dans deux groupes de la même vague ne compte qu’une fois : le joueur lit
 * « 3 vagues », pas « 5 groupes ». L’ordre suit la première apparition dans le niveau.
 */
export function resumerEnnemisDuNiveau(niveau: Niveau): EnnemiDuNiveau[] {
  const parId = new Map<string, { vagues: number; boss: boolean }>();
  for (const vague of niveau.vagues) {
    const dejaCompte = new Set<string>();
    for (const groupe of vague.groupes) {
      const courant = parId.get(groupe.ennemiId) ?? { vagues: 0, boss: false };
      if (!dejaCompte.has(groupe.ennemiId)) {
        courant.vagues += 1;
        dejaCompte.add(groupe.ennemiId);
      }
      if (vague.boss) courant.boss = true;
      parId.set(groupe.ennemiId, courant);
    }
  }
  return [...parId.entries()].map(([id, resume]) => ({ id, ...resume }));
}

export function BriefingNiveau({
  niveau,
  notePaysage,
  idTitre = 'briefing-titre',
  className,
}: {
  readonly niveau: Niveau;
  /**
   * Identifiant du titre (cible de `aria-labelledby`). L'écran de jeu monte le briefing deux fois
   * en portrait (sous le plateau et dans le tiroir « Infos ») : chaque instance a le sien.
   */
  readonly idTitre?: string;
  /** Classe supplémentaire (variante d'emplacement). */
  readonly className?: string;
  /**
   * Note affichée à droite du titre du briefing — l'écran de jeu y place l'invitation à passer en
   * paysage (DECISIONS A-11). Optionnelle : le briefing reste utilisable seul (tests, réemploi).
   */
  readonly notePaysage?: ReactNode;
}) {
  const { t } = useTranslation();
  const ennemis = resumerEnnemisDuNiveau(niveau);

  return (
    <section
      className={['briefing', className].filter(Boolean).join(' ')}
      aria-labelledby={idTitre}
      data-briefing-niveau={niveau.id}
    >
      <div className="briefing__entete">
        <h2 className="briefing__titre" id={idTitre}>
          {t('jeu.briefing.titre')}
        </h2>
        {notePaysage}
      </div>
      <p className="briefing__description" data-description-niveau="oui">
        {t(cleTraductionContenu(`content.niveau.${niveau.id}.desc`))}
      </p>
      <p className="briefing__da" data-da={niveau.da}>
        <span className="briefing__da-etiquette">{t('jeu.briefing.directionArtistique')}</span>{' '}
        <span className="briefing__da-nom">
          {t(cleTraductionContenu(`content.da.${niveau.da}.name`))}
        </span>
        <span className="briefing__da-separateur" aria-hidden="true">
          {' — '}
        </span>
        <span className="briefing__da-desc">
          {t(cleTraductionContenu(`content.da.${niveau.da}.desc`))}
        </span>
      </p>
      <details className="briefing__bestiaire">
        <summary>{t('jeu.briefing.bestiaireTitre')}</summary>
        <ul className="briefing__liste">
          {ennemis.map((ennemi) => (
            <li className="briefing__ennemi" key={ennemi.id} data-ennemi={ennemi.id}>
              <p className="briefing__ennemi-entete">
                <span className="briefing__ennemi-nom">
                  {t(cleTraductionContenu(`content.ennemi.${ennemi.id}.name`))}
                </span>
                <span className="briefing__ennemi-vagues">
                  {t('jeu.briefing.bestiaireVagues', { count: ennemi.vagues })}
                </span>
                {ennemi.boss ? (
                  <span className="briefing__ennemi-boss">{t('jeu.briefing.boss')}</span>
                ) : null}
              </p>
              <p className="briefing__ennemi-desc">
                {t(cleTraductionContenu(`content.ennemi.${ennemi.id}.desc`))}
              </p>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
